import moment from "moment";
import config from "config/config";
import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  stringFormattingToCurrency,
  getPaymentMethodAndTypeByPaymentHistoryItemCode,
} from "js/formatting";
import { getToken } from "js/formatting/getToken";
import { isFlagEnabled } from "js/utilities";

export const getPaymentHistory = createAsyncThunk(
  "getPaymentHistory",
  async (payload, { fulfillWithValue }) => {
    let allPaymentHistoryInPayload = [];

    const VALID_PAYMENT_HISTORY_ITEM_CODE =
      config.VALID_PAYMENT_HISTORY_ITEM_CODE?.split(",").map((code) =>
        parseInt(code)
      );

    const VALID_WITHDRAWAL_HISTORY_ITEM_CODE =
      config.VALID_WITHDRAWAL_HISTORY_ITEM_CODE?.split(",").map((code) =>
        parseInt(code)
      );

    for (let i = 0; i < payload.length; i++) {
      const currentPolicy = payload[i];

      let options = {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "Ocp-Apim-Subscription-Key": config.ODS_API_KEY,
          Authorization: `bearer ${getToken()}`,
        },
        body: JSON.stringify({
          gXdpApi: {
            correlationID: moment().format("YYYY-MM-DD HH:MM:SS"),
            transactionType: {
              eventName: "GETPAYMENTHISTORY",
              sourceNameSpace: "ODS",
              targetNameSpace: "GIDP",
              operation: "GET",
            },
            gIdpPayLoad: {
              policy: {
                policyId: currentPolicy.contractId,
                taxId: "",
                beginDate: moment()
                  .subtract(parseInt(config.PAYMENT_DURATION), "month")
                  .format("MM/DD/YYYY"),
                endDate: moment().format("MM/DD/YYYY"),
              },
              addOnProperties: {
                lifesys: {
                  policyHolder: {
                    company: config.COMPANY,
                    clientName: config.CLIENTNAME,
                    system: "LSN",
                    lifeSysDataset: "",
                    showLogTimeStamp: "N",
                    showTotalRequestTime: "Y",
                  },
                },
              },
            },
            addOnProperties: {},
          },
        }),
      };
      const response = await fetch(
        config.ODS_API_BASE_URL + "/ce-runtime/GetPaymentHistory",
        options
      );

      if (!response.ok && response.status === 401) throw response.status;

      const data = await response.json();

      if (data?.gXdpApi.success) {
        if (currentPolicy?.rawResponse) {
          allPaymentHistoryInPayload.push(
            ...data?.gXdpApi?.gIdpPayLoad?.paymentHistories
          );
        } else {
          let validTransactions = [];
          data?.gXdpApi?.gIdpPayLoad?.paymentHistories.forEach(
            (transaction) => {
              const currentTransaction = transaction?.transactions[0];
              const creditCardDecline = transaction?.creditCardDecline;

              // only include payment transactions that is part of the valid item code
              if (
                VALID_PAYMENT_HISTORY_ITEM_CODE?.includes(
                  parseInt(currentTransaction.itemCode)
                )
              ) {
                let paymentAmount = getPaymentAmount(currentTransaction);
                // add formatted payment history to complete array
                if (paymentAmount != 0) {
                  validTransactions.push({
                    ...currentPolicy,
                    paymentMethod:
                      currentTransaction.paymentMethod?.policyPaymentMethod,
                    amount: stringFormattingToCurrency(Math.abs(paymentAmount)),
                    method: getPaymentMethodAndTypeByPaymentHistoryItemCode(
                      currentTransaction.itemCode
                    ).method,
                    type: getPaymentMethodAndTypeByPaymentHistoryItemCode(
                      currentTransaction.itemCode
                    ).type,
                    postedDate: moment(currentTransaction.systemDate).format(
                      "MM/DD/YYYY"
                    ),
                  });
                }
              } else if (
                VALID_WITHDRAWAL_HISTORY_ITEM_CODE?.includes(
                  parseInt(currentTransaction.withdrawals?.itemCode)
                )
              ) {
                let paymentAmount = getPaymentAmount(currentTransaction, true);

                if (paymentAmount != 0) {
                  validTransactions.push({
                    ...currentPolicy,
                    paymentMethod:
                      currentTransaction.paymentMethod?.policyPaymentMethod,
                    amount: `${stringFormattingToCurrency(paymentAmount)}`,
                    method: currentTransaction.withdrawals?.withdrawalType,
                    type: getPaymentMethodAndTypeByPaymentHistoryItemCode(
                      currentTransaction.itemCode
                    ).type,
                    postedDate: moment(
                      currentTransaction.withdrawals?.withdrawalDate
                    ).format("MM/DD/YYYY"),
                  });
                }
              }
              if (
                isFlagEnabled("ENABLE_SHOWING_CREDIT_CARD_DECLINED") &&
                creditCardDecline
              ) {
                creditCardDecline.forEach((creditCardDate) => {
                  validTransactions.push({
                    ...currentPolicy,
                    paymentMethod:
                      currentTransaction.paymentMethod?.policyPaymentMethod,
                    amount: stringFormattingToCurrency(
                      Math.abs(currentTransaction.modalPremiumAmountTotal) ?? 0
                    ),
                    method: "Credit Card",
                    type: "Declined",
                    postedDate: moment(
                      creditCardDate.creditCardDeclineDate
                    ).format("MM/DD/YYYY"),
                  });
                });
              }
            }
          );

          if (validTransactions?.length > 0) {
            // Sort in descending order
            const sortedPaymentHistory = validTransactions.sort(
              (a, b) => moment(b?.postedDate) - moment(a?.postedDate)
            );
            // add sorted payment history to final array
            allPaymentHistoryInPayload.push(...sortedPaymentHistory);
          }
        }
      }
    }
    return fulfillWithValue(allPaymentHistoryInPayload);
  }
);

const getPaymentAmount = (currentTransaction, isWithdrawals = false) => {
  const accountCategoryValues = getAccountCategoryValues(isWithdrawals);

  const supplementalBenefitsAndCharges =
    currentTransaction?.supplementalBenefitsAndCharges.filter((item) =>
      accountCategoryValues.includes(item?.accountCategory?.toLowerCase())
    );

  return (
    getTransactionAmount(supplementalBenefitsAndCharges, isWithdrawals) ?? 0
  );
};

// Helper function to determine account category values
const getAccountCategoryValues = (isWithdrawals) => {
  return [
    isWithdrawals
      ? config.CASH_OUT_CHECK_EFT_ACCOUNT_CATEGORY_VALUE.toLowerCase()
      : config.CASH_CHECK_EFT_ACCOUNT_CATEGORY_VALUE.toLowerCase(),
    config.POLICY_SUSPENSE_VALUE.toLowerCase(),
  ];
};

// Helper function to get transaction amount for withdrawals
const getTransactionAmount = (
  supplementalBenefitsAndCharges,
  isWithdrawals
) => {
  if (supplementalBenefitsAndCharges?.length > 1) {
    if (isWithdrawals)
      return (
        supplementalBenefitsAndCharges.find(
          (item) =>
            item?.accountCategory?.toLowerCase() ===
            config.CASH_OUT_CHECK_EFT_ACCOUNT_CATEGORY_VALUE.toLowerCase()
        )?.transactionAmount ?? 0
      );
    else
      return (
        supplementalBenefitsAndCharges.find(
          (item) =>
            item?.accountCategory?.toLowerCase() ===
            config.CASH_CHECK_EFT_ACCOUNT_CATEGORY_VALUE.toLowerCase()
        )?.transactionAmount ?? 0
      );
  }

  return supplementalBenefitsAndCharges[0]?.transactionAmount;
};
